import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, LayersControl, AttributionControl, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { MagnifyingGlass, MapPin, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const pin = L.divIcon({
  className: "",
  html: `<div style="background:#C5A059;width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #0B2545;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
  iconSize: [26, 26], iconAnchor: [13, 26],
});

function Recenter({ lat, lng }) {
  const map = useMap();
  React.useEffect(() => { if (lat && lng) map.setView([lat, lng], 16); }, [lat, lng, map]);
  return null;
}

export default function LocationResolver({ onConfirm, initial = null }) {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [cands, setCands] = useState([]);
  const [point, setPoint] = useState(initial);

  const resolve = async () => {
    if (!q.trim()) return;
    setLoading(true); setCands([]);
    try {
      const { data } = await api.get("/places/resolve", { params: { q, lang: i18n.language } });
      if (!data.candidates || data.candidates.length === 0) toast.info(t("places.noResults", "No results"));
      else if (data.candidates.length === 1) setPoint(data.candidates[0]);
      else setCands(data.candidates);
    } catch (e) { toast.error(t("places.noResults", "No results")); }
    finally { setLoading(false); }
  };

  const refreshAddress = async (lat, lng) => {
    try {
      const { data } = await api.get("/places/resolve", { params: { q: `${lat},${lng}`, lang: i18n.language } });
      if (data.candidates?.[0]) setPoint(data.candidates[0]);
    } catch (e) { /* keep prior meta */ }
  };

  const onDragEnd = (e) => {
    const { lat, lng } = e.target.getLatLng();
    setPoint((p) => ({ ...p, lat, lng }));
    refreshAddress(lat, lng);
  };

  return (
    <div className="space-y-3" data-testid="location-resolver">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">{t("places.searchLocation", "Search location")}</div>
        <div className="flex gap-2">
          <Input data-testid="resolver-input" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolve(); } }}
            placeholder={t("places.resolverPlaceholder", "Paste coordinates, a Google Maps/Earth link, Plus Code, address or place name…")}
            className="rounded-sm" />
          <Button onClick={resolve} data-testid="resolver-btn" disabled={loading} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <MagnifyingGlass size={16} className={loading ? "animate-pulse" : ""} />
          </Button>
        </div>
      </div>

      {cands.length > 0 && (
        <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="resolver-candidates">
          <div className="px-3 py-1.5 text-xs text-slate-500 bg-slate-50">{t("places.candidates", "Select the correct match")}</div>
          {cands.map((c, i) => (
            <button key={i} onClick={() => { setPoint(c); setCands([]); }} data-testid={`resolver-candidate-${i}`}
              className="w-full text-left px-3 py-2.5 flex items-start gap-2 hover:bg-slate-50">
              <MapPin size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
              <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{c.name}</div>
                <div className="text-xs text-slate-500 truncate">{c.label}</div></div>
            </button>
          ))}
        </div>
      )}

      {point && point.lat && point.lng && (
        <div className="space-y-2">
          <div style={{ height: "260px" }} className="rounded-sm overflow-hidden border border-slate-200" data-testid="resolver-map">
            <MapContainer center={[point.lat, point.lng]} zoom={16} style={{ height: "100%", width: "100%" }} scrollWheelZoom attributionControl={false}>
              <AttributionControl prefix={false} />
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name={t("places.satellite", "Satellite")}>
                  <TileLayer attribution="Tiles &copy; Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name={t("places.standard", "Standard")}>
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </LayersControl.BaseLayer>
              </LayersControl>
              <Recenter lat={point.lat} lng={point.lng} />
              <Marker position={[point.lat, point.lng]} icon={pin} draggable eventHandlers={{ dragend: onDragEnd }} />
            </MapContainer>
          </div>
          <div className="text-xs text-slate-500">{t("places.dragToAdjust", "Drag the marker to the exact point")}</div>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm" data-testid="resolver-selected">
            <div className="grid grid-cols-3 gap-2">
              <div><span className="text-slate-400 text-xs">{t("places.city", "City")}</span><div className="font-medium truncate">{point.city || "—"}</div></div>
              <div><span className="text-slate-400 text-xs">{t("places.region", "Region")}</span><div className="font-medium truncate">{point.region || "—"}</div></div>
              <div><span className="text-slate-400 text-xs">{t("places.country", "Country")}</span><div className="font-medium truncate">{point.country || "—"}</div></div>
            </div>
            <div className="text-xs text-slate-400 mt-2">{t("places.coordinates", "Coordinates")}: {Number(point.lat).toFixed(5)}, {Number(point.lng).toFixed(5)}</div>
          </div>
          <Button onClick={() => onConfirm && onConfirm(point)} data-testid="resolver-confirm" className="w-full rounded-sm bg-[#C5A059] hover:bg-[#b08d4a] text-white">
            <Check size={16} className="mr-1.5" />{t("places.confirmPoint", "Confirm this point")}
          </Button>
        </div>
      )}
    </div>
  );
}
